// pages/Tarifas.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber,
  DatePicker,
  Divider,
  Collapse,
  Descriptions,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  CalendarOutlined,
  EuroOutlined,
  FileTextOutlined,
  DollarOutlined,
  PercentageOutlined,
  CopyOutlined,
  ExportOutlined,
  FilterOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const Tarifas = () => {
    const navigate = useNavigate();
  // Estados principales
  const [activeTab, setActiveTab] = useState('contratos');
  const [loading, setLoading] = useState(false);
  
  // Estados para Contratos
  const [contratos, setContratos] = useState([]);
  const [modalContratoVisible, setModalContratoVisible] = useState(false);
  const [modalContratoType, setModalContratoType] = useState('create');
  const [currentContrato, setCurrentContrato] = useState(null);
  const [formContrato] = Form.useForm();
  
  // Estados para Líneas de Contrato (diarias)
  const [lineasContrato, setLineasContrato] = useState([]);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [modalLineaVisible, setModalLineaVisible] = useState(false);
  const [modalLineaType, setModalLineaType] = useState('create');
  const [currentLinea, setCurrentLinea] = useState(null);
  const [formLinea] = Form.useForm();
  
  // Estados para LinContrato (rangos)
  const [linContratos, setLinContratos] = useState([]);
  const [modalLinContratoVisible, setModalLinContratoVisible] = useState(false);
  const [modalLinContratoType, setModalLinContratoType] = useState('create');
  const [currentLinContrato, setCurrentLinContrato] = useState(null);
  const [formLinContrato] = Form.useForm();
  
  // Estados para filtros y paginación
  const [searchText, setSearchText] = useState('');
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  
  // Paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `${total} registros`
  });

  // Columnas para Contratos
  const columnsContratos = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 100,
      sorter: (a, b) => a.Codigo.localeCompare(b.Codigo),
      render: (codigo) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {codigo}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Moneda',
      dataIndex: 'Moneda',
      key: 'Moneda',
      width: 80,
      render: (moneda) => (
        <Tag color="cyan">{moneda}</Tag>
      ),
    },
    {
      title: 'Tipo IVA',
      dataIndex: 'TipoIVA',
      key: 'TipoIVA',
      width: 100,
      render: (tipo) => (
        <Tag color="purple">IVA {tipo}</Tag>
      ),
    },
    {
      title: 'Bloqueado',
      dataIndex: 'Bloqueado',
      key: 'Bloqueado',
      width: 100,
      render: (bloqueado) => (
        bloqueado ? (
          <Badge status="error" text="Bloqueado" />
        ) : (
          <Badge status="success" text="Activo" />
        )
      ),
    },
    {
      title: 'Validez Desde',
      dataIndex: 'FechaValidezDesde',
      key: 'FechaValidezDesde',
      width: 120,
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Oferta',
      dataIndex: 'EsOferta',
      key: 'EsOferta',
      width: 80,
      render: (oferta) => (
        oferta ? (
          <Tag color="orange">Sí</Tag>
        ) : (
          <Tag color="default">No</Tag>
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver líneas de precios">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleVerLineas(record)}
            />
          </Tooltip>
        <Tooltip title="Gestionar líneas">
            <Button
                type="primary"
                icon={<FileTextOutlined />}
                size="small"
                onClick={() => navigate(`/lineas-contrato/${record.Codigo}`)}
            />
        </Tooltip>          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditContrato(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar contrato?"
            description="Esta acción eliminará el contrato y todas sus líneas"
            onConfirm={() => handleDeleteContrato(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Columnas para Líneas de Contrato (diarias)
  const columnsLineasContrato = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      sorter: (a, b) => new Date(a.fecha) - new Date(b.fecha),
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Precio MP',
      dataIndex: 'preciomp',
      key: 'preciomp',
      width: 100,
      render: (precio) => (
        <Text strong style={{ color: '#1890ff' }}>
          {precio.toFixed(2)} €
        </Text>
      ),
    },
    {
      title: 'Precio HD',
      dataIndex: 'preciohd',
      key: 'preciohd',
      width: 100,
      render: (precio) => precio.toFixed(2),
    },
    {
      title: 'Precio IN',
      dataIndex: 'PrecioIn',
      key: 'PrecioIn',
      width: 100,
      render: (precio) => precio.toFixed(2),
    },
    {
      title: 'Niño 1',
      dataIndex: 'precion11',
      key: 'precion11',
      width: 100,
      render: (precio) => precio > 0 ? precio.toFixed(2) : '-',
    },
    {
      title: 'DUI',
      dataIndex: 'DobleUsoIndividual',
      key: 'DobleUsoIndividual',
      width: 100,
      render: (dui) => dui > 0 ? dui.toFixed(2) : '-',
    },
    {
      title: 'Mín. Noches',
      dataIndex: 'MinNoches',
      key: 'MinNoches',
      width: 100,
      render: (noches) => noches > 0 ? noches : '-',
    },
    {
      title: 'Weekend',
      dataIndex: 'esweekend',
      key: 'esweekend',
      width: 100,
      render: (weekend) => (
        weekend ? (
          <Tag color="purple">Sí</Tag>
        ) : (
          <Tag color="default">No</Tag>
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditLinea(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar línea?"
            onConfirm={() => handleDeleteLinea(record.fecha)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Columnas para LinContrato (rangos)
  const columnsLinContrato = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      render: (codigo) => (
        <Tag color="blue">{codigo}</Tag>
      ),
    },
    {
      title: 'Fecha Desde',
      dataIndex: 'FechaDesde',
      key: 'FechaDesde',
      width: 120,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Fecha Hasta',
      dataIndex: 'FechaHasta',
      key: 'FechaHasta',
      width: 120,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Rango',
      key: 'rango',
      width: 150,
      render: (_, record) => {
        const desde = dayjs(record.FechaDesde);
        const hasta = dayjs(record.FechaHasta);
        const dias = hasta.diff(desde, 'day') + 1;
        return `${dias} días`;
      },
    },
    {
      title: 'Precio MP',
      dataIndex: 'preciomp',
      key: 'preciomp',
      width: 100,
      render: (precio) => (
        <Text strong style={{ color: '#1890ff' }}>
          {precio.toFixed(2)} €
        </Text>
      ),
    },
    {
      title: 'Precio HD',
      dataIndex: 'preciohd',
      key: 'preciohd',
      width: 100,
      render: (precio) => precio.toFixed(2),
    },
    {
      title: 'Weekend',
      dataIndex: 'esweekend',
      key: 'esweekend',
      width: 100,
      render: (weekend) => (
        weekend ? (
          <Tag color="purple">Sí</Tag>
        ) : (
          <Tag color="default">No</Tag>
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewLinContrato(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditLinContrato(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar rango?"
            onConfirm={() => handleDeleteLinContrato(record.codigo, record.FechaDesde)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Efectos iniciales
  useEffect(() => {
    fetchContratos();
  }, []);

  useEffect(() => {
    if (selectedContrato) {
      fetchLineasContrato(selectedContrato.Codigo);
      fetchLinContrato(selectedContrato.Codigo);
    }
  }, [selectedContrato]);

  // Funciones para Contratos
  const fetchContratos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/contratos`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchText }
      });

      setContratos(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.totalRecords || response.data.data?.length || 0
      }));
    } catch (error) {
      console.error('❌ Error cargando contratos:', error);
      message.error('Error al cargar los contratos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLineasContrato = async (codigoContrato) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/lineascontrato/${codigoContrato}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fechaDesde: fechaDesde ? fechaDesde.format('YYYY-MM-DD') : null,
          fechaHasta: fechaHasta ? fechaHasta.format('YYYY-MM-DD') : null
        }
      });

      setLineasContrato(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando líneas:', error);
      message.error('Error al cargar las líneas del contrato');
    }
  };

  const fetchLinContrato = async (codigoContrato) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/lincontrato/${codigoContrato}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLinContratos(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando lincontrato:', error);
      message.error('Error al cargar la vista consolidada');
    }
  };

  const handleCreateContrato = () => {
    setModalContratoType('create');
    setCurrentContrato(null);
    formContrato.resetFields();
    
    // Valores por defecto
    formContrato.setFieldsValue({
      Moneda: 'EUR',
      TipoIVA: '1',
      Aplicado: 'I',
      TipoPrecio: 'P',
      Bloqueado: false,
      EsOferta: false,
      SuplementosNinosSiempre: false,
      FechaValidezDesde: dayjs(),
      FechaCreacion: dayjs(),
      esLunes: false,
      esMartes: false,
      esMiercoles: false,
      esJueves: false,
      esViernes: false,
      esSabado: false,
      esDomingo: false
    });
    
    setModalContratoVisible(true);
  };

  const handleEditContrato = (record) => {
    setModalContratoType('edit');
    setCurrentContrato(record);
    
    const formValues = {
      ...record,
      FechaValidezDesde: record.FechaValidezDesde ? dayjs(record.FechaValidezDesde) : null,
      FechaValidez: record.FechaValidez ? dayjs(record.FechaValidez) : null,
      FechaValidezEstanciaDesde: record.FechaValidezEstanciaDesde ? dayjs(record.FechaValidezEstanciaDesde) : null,
      FechaValidezEstanciaHasta: record.FechaValidezEstanciaHasta ? dayjs(record.FechaValidezEstanciaHasta) : null,
      FechaCreacion: record.FechaCreacion ? dayjs(record.FechaCreacion) : null
    };
    
    formContrato.setFieldsValue(formValues);
    setModalContratoVisible(true);
  };

  const handleSaveContrato = async () => {
    try {
      const values = await formContrato.validateFields();
      const token = localStorage.getItem('token');
      
      // Formatear fechas
      const formattedValues = {
        ...values,
        FechaValidezDesde: values.FechaValidezDesde ? values.FechaValidezDesde.format('YYYY-MM-DD') : null,
        FechaValidez: values.FechaValidez ? values.FechaValidez.format('YYYY-MM-DD') : null,
        FechaValidezEstanciaDesde: values.FechaValidezEstanciaDesde ? values.FechaValidezEstanciaDesde.format('YYYY-MM-DD') : null,
        FechaValidezEstanciaHasta: values.FechaValidezEstanciaHasta ? values.FechaValidezEstanciaHasta.format('YYYY-MM-DD') : null,
        FechaCreacion: values.FechaCreacion ? values.FechaCreacion.format('YYYY-MM-DD') : null
      };
      
      if (modalContratoType === 'create') {
        await axios.post(`${BASE_URL}/api/contratos`, formattedValues, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Contrato creado correctamente');
      } else {
        await axios.put(`${BASE_URL}/api/contratos/${currentContrato.Codigo}`, formattedValues, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Contrato actualizado correctamente');
      }
      
      setModalContratoVisible(false);
      formContrato.resetFields();
      fetchContratos();
      
    } catch (error) {
      console.error('❌ Error guardando contrato:', error);
      message.error(error.response?.data?.error || 'Error al guardar el contrato');
    }
  };

  const handleDeleteContrato = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/contratos/${codigo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContratos(contratos.filter(c => c.Codigo !== codigo));
      message.success('Contrato eliminado correctamente');
      
      if (selectedContrato?.Codigo === codigo) {
        setSelectedContrato(null);
        setLineasContrato([]);
        setLinContratos([]);
      }
    } catch (error) {
      console.error('❌ Error eliminando contrato:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el contrato');
    }
  };

  const handleVerLineas = (contrato) => {
    setSelectedContrato(contrato);
    setActiveTab('lineas');
  };

  // Funciones para Líneas de Contrato
  const handleCreateLinea = () => {
    if (!selectedContrato) {
      message.warning('Seleccione un contrato primero');
      return;
    }
    
    setModalLineaType('create');
    setCurrentLinea(null);
    formLinea.resetFields();
    
    // Valores por defecto
    formLinea.setFieldsValue({
      codigo: selectedContrato.Codigo,
      fecha: dayjs(),
      preciomp: 0,
      preciohd: 0,
      PrecioIn: 0,
      DobleUsoIndividual: 0,
      MinNoches: 0,
      esweekend: false,
      DUISuplemento: false,
      PrecioINPorcentaje: false,
      DUIPorcentaje: false
    });
    
    setModalLineaVisible(true);
  };

  const handleEditLinea = (record) => {
    setModalLineaType('edit');
    setCurrentLinea(record);
    
    const formValues = {
      ...record,
      fecha: dayjs(record.fecha)
    };
    
    formLinea.setFieldsValue(formValues);
    setModalLineaVisible(true);
  };

  const handleSaveLinea = async () => {
    try {
      const values = await formLinea.validateFields();
      const token = localStorage.getItem('token');
      
      // Formatear fecha
      const formattedValues = {
        ...values,
        fecha: values.fecha.format('YYYY-MM-DD')
      };
      
      if (modalLineaType === 'create') {
        await axios.post(`${BASE_URL}/api/lineascontrato`, formattedValues, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Línea creada correctamente');
      } else {
        await axios.put(`${BASE_URL}/api/lineascontrato/${values.codigo}/${values.fecha}`, formattedValues, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Línea actualizada correctamente');
      }
      
      setModalLineaVisible(false);
      formLinea.resetFields();
      fetchLineasContrato(selectedContrato.Codigo);
      fetchLinContrato(selectedContrato.Codigo);
      
    } catch (error) {
      console.error('❌ Error guardando línea:', error);
      message.error(error.response?.data?.error || 'Error al guardar la línea');
    }
  };

  const handleDeleteLinea = async (fecha) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/lineascontrato/${selectedContrato.Codigo}/${fecha}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLineasContrato(lineasContrato.filter(l => l.fecha !== fecha));
      message.success('Línea eliminada correctamente');
      
      // Actualizar vista consolidada
      fetchLinContrato(selectedContrato.Codigo);
    } catch (error) {
      console.error('❌ Error eliminando línea:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la línea');
    }
  };

  // Funciones para LinContrato
  const handleCreateLinContrato = () => {
    if (!selectedContrato) {
      message.warning('Seleccione un contrato primero');
      return;
    }
    
    setModalLinContratoType('create');
    setCurrentLinContrato(null);
    formLinContrato.resetFields();
    
    // Valores por defecto
    const hoy = dayjs();
    formLinContrato.setFieldsValue({
      codigo: selectedContrato.Codigo,
      FechaDesde: hoy,
      FechaHasta: hoy.add(1, 'day'),
      preciomp: 0,
      preciohd: 0,
      PrecioIn: 0,
      esweekend: false,
      DUISuplemento: false
    });
    
    setModalLinContratoVisible(true);
  };

  const handleEditLinContrato = (record) => {
    setModalLinContratoType('edit');
    setCurrentLinContrato(record);
    
    const formValues = {
      ...record,
      FechaDesde: dayjs(record.FechaDesde),
      FechaHasta: dayjs(record.FechaHasta)
    };
    
    formLinContrato.setFieldsValue(formValues);
    setModalLinContratoVisible(true);
  };

  const handleViewLinContrato = (record) => {
    Modal.info({
      title: `Rango de Precios - ${record.codigo}`,
      width: 800,
      content: (
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Código" span={2}>
            <Tag color="blue">{record.codigo}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Desde">
            {dayjs(record.FechaDesde).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Hasta">
            {dayjs(record.FechaHasta).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Días">
            {dayjs(record.FechaHasta).diff(dayjs(record.FechaDesde), 'day') + 1}
          </Descriptions.Item>
          <Descriptions.Item label="Weekend">
            {record.esweekend ? <Tag color="purple">Sí</Tag> : <Tag color="default">No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="MP" span={1}>
            <Text strong style={{ color: '#1890ff' }}>{record.preciomp.toFixed(2)} €</Text>
          </Descriptions.Item>
          <Descriptions.Item label="HD" span={1}>
            {record.preciohd.toFixed(2)} €
          </Descriptions.Item>
          <Descriptions.Item label="IN" span={1}>
            {record.PrecioIn.toFixed(2)} €
          </Descriptions.Item>
          <Descriptions.Item label="DUI" span={1}>
            {record.DobleUsoIndividual > 0 ? `${record.DobleUsoIndividual.toFixed(2)} €` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Mín. Noches" span={1}>
            {record.MinNoches > 0 ? record.MinNoches : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Niño 1" span={1}>
            {record.precion11 > 0 ? `${record.precion11.toFixed(2)} €` : '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
      onOk() {},
    });
  };

  const handleSaveLinContrato = async () => {
    try {
      const values = await formLinContrato.validateFields();
      const token = localStorage.getItem('token');
      
      // Validar fechas
      if (values.FechaDesde.isAfter(values.FechaHasta)) {
        message.error('La fecha desde debe ser anterior a la fecha hasta');
        return;
      }
      
      // Formatear fechas
      const formattedValues = {
        ...values,
        FechaDesde: values.FechaDesde.format('YYYY-MM-DD'),
        FechaHasta: values.FechaHasta.format('YYYY-MM-DD')
      };
      
      if (modalLinContratoType === 'create') {
        await axios.post(`${BASE_URL}/api/lincontrato`, formattedValues, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Rango creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/lincontrato/${values.codigo}/${currentLinContrato.FechaDesde}`, 
          formattedValues,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Rango actualizado correctamente');
      }
      
      setModalLinContratoVisible(false);
      formLinContrato.resetFields();
      fetchLinContrato(selectedContrato.Codigo);
      
    } catch (error) {
      console.error('❌ Error guardando rango:', error);
      message.error(error.response?.data?.error || 'Error al guardar el rango');
    }
  };

  const handleDeleteLinContrato = async (codigo, fechaDesde) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/lincontrato/${codigo}/${fechaDesde}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLinContratos(linContratos.filter(l => !(l.codigo === codigo && l.FechaDesde === fechaDesde)));
      message.success('Rango eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando rango:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el rango');
    }
  };

  // Búsqueda
  const handleSearch = () => {
    fetchContratos();
  };

  const handleResetSearch = () => {
    setSearchText('');
    fetchContratos();
  };

  // Filtros para líneas
  const handleFiltrarLineas = () => {
    if (!selectedContrato) return;
    fetchLineasContrato(selectedContrato.Codigo);
  };

  const handleResetFiltrosLineas = () => {
    setFechaDesde(null);
    setFechaHasta(null);
    if (selectedContrato) {
      fetchLineasContrato(selectedContrato.Codigo);
    }
  };

  // Renderizar contenido según tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'contratos':
        return (
          <>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="Buscar por código o descripción"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                  onPressEnter={handleSearch}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Buscar
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetSearch}
                >
                  Restablecer
                </Button>
              </Space>
            </div>
            
            <Table
              columns={columnsContratos}
              dataSource={contratos}
              rowKey="Codigo"
              loading={loading}
              pagination={pagination}
              scroll={{ x: 1000 }}
              onRow={(record) => ({
                onClick: () => setSelectedContrato(record),
                style: { 
                  cursor: 'pointer',
                  backgroundColor: selectedContrato?.Codigo === record.Codigo ? '#e6f7ff' : 'inherit'
                }
              })}
            />
          </>
        );
        
      case 'lineas':
        if (!selectedContrato) {
          return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <p>Seleccione un contrato para ver sus líneas de precios</p>
            </div>
          );
        }
        
        return (
          <>
            {/* Info del contrato seleccionado */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16} align="middle">
                <Col>
                  <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>
                    {selectedContrato.Codigo}
                  </Tag>
                </Col>
                <Col flex="auto">
                  <Text strong>{selectedContrato.Descripcion}</Text>
                  <div style={{ fontSize: 12, color: '#666' }}>                    
                    Moneda: {selectedContrato.Moneda} | 
                    IVA: {selectedContrato.TipoIVA}
                  </div>
                </Col>
                <Col>
                  <Button 
                    type="link" 
                    onClick={() => setActiveTab('contratos')}
                  >
                    Cambiar contrato
                  </Button>
                </Col>
              </Row>
            </Card>
            
            {/* Tabs internos para Líneas diarias vs Rango */}
            <Tabs 
              defaultActiveKey="diarias" 
              type="card"
              style={{ marginBottom: 16 }}
            >
              <TabPane tab="Líneas Diarias" key="diarias">
                {/* Filtros para líneas diarias */}
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <DatePicker
                      placeholder="Fecha desde"
                      value={fechaDesde}
                      onChange={setFechaDesde}
                      format="DD/MM/YYYY"
                    />
                    <DatePicker
                      placeholder="Fecha hasta"
                      value={fechaHasta}
                      onChange={setFechaHasta}
                      format="DD/MM/YYYY"
                    />
                    <Button
                      type="primary"
                      icon={<FilterOutlined />}
                      onClick={handleFiltrarLineas}
                    >
                      Filtrar
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleResetFiltrosLineas}
                    >
                      Limpiar
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateLinea}
                    >
                      Nueva Línea
                    </Button>
                  </Space>
                </div>
                
                <Table
                  columns={columnsLineasContrato}
                  dataSource={lineasContrato}
                  rowKey="fecha"
                  size="small"
                  scroll={{ x: 900 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <Text strong>Total: {lineasContrato.length} días</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text strong>
                          {lineasContrato.reduce((sum, item) => sum + (item.preciomp || 0), 0).toFixed(2)} €
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </TabPane>
              
              <TabPane tab="Rangos Consolidados" key="rangos">
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateLinContrato}
                  >
                    Nuevo Rango
                  </Button>
                </div>
                
                <Table
                  columns={columnsLinContrato}
                  dataSource={linContratos}
                  rowKey={(record) => `${record.codigo}-${record.FechaDesde}`}
                  size="small"
                  scroll={{ x: 1000 }}
                />
              </TabPane>
            </Tabs>
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <>
      <PageContainer
        title="Gestión de Tarifas"
        subtitle="Contratos y precios"
        icon={<EuroOutlined />}
        loading={loading}
        onRefresh={() => {
          if (activeTab === 'contratos') {
            fetchContratos();
          } else if (selectedContrato) {
            fetchLineasContrato(selectedContrato.Codigo);
            fetchLinContrato(selectedContrato.Codigo);
          }
        }}
        onCreate={activeTab === 'contratos' ? handleCreateContrato : null}
        extra={
          <Space>
            {activeTab === 'lineas' && selectedContrato && (
              <Tag color="blue">
                Contrato: {selectedContrato.Codigo}
              </Tag>
            )}
            <Statistic
              title="Total"
              value={activeTab === 'contratos' ? pagination.total : lineasContrato.length}
              prefix={activeTab === 'contratos' ? <FileTextOutlined /> : <CalendarOutlined />}
              valueStyle={{ fontSize: '16px' }}
            />
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="large"
        >
          <TabPane tab="Contratos" key="contratos" />
          {/* <TabPane 
            tab="Líneas de Precios" 
            key="lineas" 
            disabled={!selectedContrato}
          /> */}
        </Tabs>
        
        {renderTabContent()}
      </PageContainer>

      {/* Modal para Contratos */}
      <Modal
        title={modalContratoType === 'create' ? 'Nuevo Contrato' : 'Editar Contrato'}
        open={modalContratoVisible}
        onOk={handleSaveContrato}
        onCancel={() => {
          setModalContratoVisible(false);
          formContrato.resetFields();
        }}
        width={700}
        okText={modalContratoType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
      >
        <Form
          form={formContrato}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[{ required: true, message: 'Código requerido' }]}
              >
                <Input placeholder="Ej: 001" disabled={modalContratoType === 'edit'} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'Descripción requerida' }]}
              >
                <Input placeholder="Descripción del contrato" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="Moneda" label="Moneda" rules={[{ required: true }]}>
                <Select>
                  <Option value="EUR">EUR</Option>
                  <Option value="USD">USD</Option>
                  <Option value="GBP">GBP</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="TipoIVA" label="Tipo IVA" rules={[{ required: true }]}>
                <Select>
                  <Option value="1">1 - General</Option>
                  <Option value="2">2 - Reducido</Option>
                  <Option value="3">3 - Superreducido</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="TipoPrecio" label="Tipo Precio" rules={[{ required: true }]}>
                <Select>
                  <Option value="P">P - Precio</Option>
                  <Option value="N">N - Neto</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="FechaValidezDesde" label="Validez Desde">
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="FechaValidez" label="Validez Hasta">
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="FechaValidezEstanciaDesde" label="Validez Estancia Desde">
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="FechaValidezEstanciaHasta" label="Validez Estancia Hasta">
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
            </Row>           
            <Row gutter={16}> 
                <Col span={8}>
                    <Form.Item name="FechaCreacion" label="Fecha Creación"  >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
            </Row>
          
          <Divider orientation="left">Configuración</Divider>
          
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="Bloqueado" label="Bloqueado" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="EsOferta" label="Es Oferta" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="SuplementosNinosSiempre" label="Supl. Niños Siempre" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="Aplicado" label="Aplicado">
                <Select>
                  <Option value="I">I - Incluido</Option>
                  <Option value="E">E - Excluido</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">Días de la Semana</Divider>
          
          <Row gutter={16}>
            {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((dia, index) => (
              <Col span={3} key={dia}>
                <Form.Item 
                  name={`es${dia.charAt(0).toUpperCase() + dia.slice(1).replace('é', 'ie').replace('á', 'a')}`}
                  valuePropName="checked"
                >
                  <Switch checkedChildren={dia.charAt(0)} unCheckedChildren={dia.charAt(0)} />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      </Modal>

      {/* Modal para Líneas de Contrato */}
      <Modal
        title={modalLineaType === 'create' ? 'Nueva Línea de Precio' : 'Editar Línea de Precio'}
        open={modalLineaVisible}
        onOk={handleSaveLinea}
        onCancel={() => {
          setModalLineaVisible(false);
          formLinea.resetFields();
        }}
        width={800}
        okText={modalLineaType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
      >
        <Form
          form={formLinea}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código Contrato"
                rules={[{ required: true }]}
              >
                <Input disabled value={selectedContrato?.Codigo} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: 'Fecha requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="esweekend" label="Weekend" valuePropName="checked">
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">Precios Principales</Divider>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="preciomp" label="Precio MP">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="preciohd" label="Precio HD">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="PrecioIn" label="Precio IN">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="DobleUsoIndividual" label="DUI">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="precion11" label="Niño 1">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="MinNoches" label="Mín. Noches">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">Configuración</Divider>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="DUISuplemento" label="DUI como Suplemento" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="PrecioINPorcentaje" label="IN como %" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="DUIPorcentaje" label="DUI como %" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal para LinContrato (rangos) */}
      <Modal
        title={modalLinContratoType === 'create' ? 'Nuevo Rango de Precios' : 'Editar Rango de Precios'}
        open={modalLinContratoVisible}
        onOk={handleSaveLinContrato}
        onCancel={() => {
          setModalLinContratoVisible(false);
          formLinContrato.resetFields();
        }}
        width={700}
        okText={modalLinContratoType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
      >
        <Form
          form={formLinContrato}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código Contrato"
                rules={[{ required: true }]}
              >
                <Input disabled value={selectedContrato?.Codigo} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="FechaDesde"
                label="Fecha Desde"
                rules={[{ required: true, message: 'Fecha requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="FechaHasta"
                label="Fecha Hasta"
                rules={[{ required: true, message: 'Fecha requerida' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="preciomp" label="Precio MP">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="preciohd" label="Precio HD">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonAfter="€"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="esweekend" label="Weekend" valuePropName="checked">
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
          
          <Collapse ghost>
            <Panel header="Más opciones" key="1">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="PrecioIn" label="Precio IN">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      addonAfter="€"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="DobleUsoIndividual" label="DUI">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      precision={2}
                      addonAfter="€"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="MinNoches" label="Mín. Noches">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={1}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </>
  );
};

export default Tarifas;